import { useCallback, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { formatDistanceToNow } from "date-fns";
import { 
  Plus, RefreshCw, Search, StickyNote, LayoutList, LayoutGrid, 
  ExternalLink, Pencil, Trash2, User, Clock
} from "lucide-react";
import { cn } from "../../lib/utils";
import { ConfirmModal } from "../../components/ui/confirm-modal";
import { NoteEditModal } from "./note-edit-modal";
import type { EditingNote } from "./note-edit-modal";
import { crmApi } from "./api";
import { avatarColorOf, initialsOf, splitNoteTitle } from "./types";
import type { CrmNote, Lead } from "./types";
import { RichText } from "./rich-text";
import { usePersistentState } from "../../hooks/use-persistent-state";
import {
  Select as _Select, SelectContent as _SelectContent, SelectItem as _SelectItem,
  SelectTrigger as _SelectTrigger, SelectValue as _SelectValue,
} from "../../components/ui/select";

const Select = _Select as any;
const SelectContent = _SelectContent as any;
const SelectItem = _SelectItem as any;
const SelectTrigger = _SelectTrigger as any;
const SelectValue = _SelectValue as any;

const PAGE_SIZES = [20, 50, 100];

const ago = (iso?: string) => {
  if (!iso) return "—";
  try { return formatDistanceToNow(new Date(iso), { addSuffix: true }); }
  catch { return "—"; }
};

/** Global notes view — sleek, dual-view (compact list / memo cards) with unified search. */
export function NotesPage() {
  const cache = useQueryClient();
  const navigate = useNavigate();
  const query = useQuery({ queryKey: ["crm-notes"], queryFn: crmApi.notes, refetchInterval: 30000 });
  const leadsQuery = useQuery({ queryKey: ["crm-note-leads"], queryFn: crmApi.leads });
  
  const [viewMode, setViewMode] = usePersistentState<"list" | "cards">("crm-notes:view-mode", "list");
  const [search, setSearch] = useState("");
  const [leadFilter, setLeadFilter] = useState("all");
  const [sort, setSort] = useState<"newest" | "oldest">("newest");
  const [pageSize, setPageSize] = useState(20);
  const [page, setPage] = useState(0);
  const [createOpen, setCreateOpen] = useState(false);
  const [editing, setEditing] = useState<EditingNote | null>(null);
  const [deleting, setDeleting] = useState<CrmNote | null>(null);
  const [error, setError] = useState("");

  const rows = useMemo(() => query.data ?? [], [query.data]);
  const leads = useMemo(() => leadsQuery.data ?? [], [leadsQuery.data]);

  const leadMap = useMemo(() => {
    const map = new Map<string, string>();
    for (const lead of leads) {
      map.set(lead.id, lead.name || "Untitled Lead");
    }
    return map;
  }, [leads]);

  const leadNameOf = useCallback((row: CrmNote) => row.leadName || leadMap.get(row.leadId) || "Lead", [leadMap]);

  const filtered = useMemo(() => {
    const all = rows;
    const q = search.trim().toLowerCase();
    
    const list = all.filter(row => {
      if (leadFilter !== "all" && row.leadId !== leadFilter) return false;
      if (!q) return true;
      
      const [title, body] = splitNoteTitle(row.notes);
      const leadName = leadNameOf(row).toLowerCase();
      const actor = (row.actorName || "").toLowerCase();
      
      return (
        title.toLowerCase().includes(q) ||
        body.toLowerCase().includes(q) ||
        row.notes.toLowerCase().includes(q) ||
        leadName.includes(q) ||
        actor.includes(q)
      );
    });

    list.sort((a, b) => sort === "newest"
      ? b.createdAt.localeCompare(a.createdAt)
      : a.createdAt.localeCompare(b.createdAt));
      
    return list;
  }, [rows, search, leadFilter, sort, leadNameOf]);

  const pageCount = Math.max(1, Math.ceil(filtered.length / pageSize));
  const safePage = Math.min(page, pageCount - 1);
  const visible = filtered.slice(safePage * pageSize, safePage * pageSize + pageSize);

  const refresh = async () => {
    await cache.invalidateQueries({ queryKey: ["crm-notes"] });
  };

  const confirmDelete = async () => {
    if (!deleting) return;
    try {
      await crmApi.deleteInteraction(deleting.leadId, deleting.id);
      await refresh();
    } catch (e) {
      setError(e instanceof Error ? e.message : "Unable to delete note.");
    } finally {
      setDeleting(null);
    }
  };

  return (
    <div className="flex h-full flex-col p-4 sm:p-5 md:p-6">
      {/* ── Top Bar: Title & Action (Clean, compact, no description) ── */}
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex items-center gap-2.5">
          <h1 className="text-sm font-semibold text-ink">Notes</h1>
          <span className="font-mono text-xs rounded-full bg-soft px-2 py-0.5 text-ink-3">
            {filtered.length}
          </span>
        </div>
        <button 
          onClick={() => setCreateOpen(true)} 
          className="flex items-center gap-1.5 rounded-lg bg-brand px-3.5 py-1.5 text-xs font-semibold text-white hover:bg-brand-hover shadow-xs transition-colors"
        >
          <Plus className="h-3.5 w-3.5" /> Create
        </button>
      </div>

      {/* ── Filter & Search Toolbar ─────────────────────────────── */}
      <div className="mt-3 flex flex-wrap items-center gap-2">
        {/* Unified Search Input */}
        <div className="relative min-w-[220px] flex-1">
          <Search className="pointer-events-none absolute left-3 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-ink-3" />
          <input
            value={search}
            onChange={e => { setSearch(e.target.value); setPage(0); }}
            placeholder="Search notes, content, or leads..."
            className="w-full rounded-lg border border-line bg-elevated py-1.5 pl-8 pr-3 text-xs text-ink outline-none focus:border-brand transition-colors"
          />
        </div>

        {/* Lead Filter Dropdown */}
        <Select value={leadFilter} onValueChange={(v: string) => { setLeadFilter(v); setPage(0); }}>
          <SelectTrigger className="w-[150px] border-line bg-elevated text-xs text-ink-2 h-8">
            <SelectValue placeholder="All leads" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all" className="text-xs">All leads</SelectItem>
            {leads.slice(0, 30).map(l => (
              <SelectItem key={l.id} value={l.id} className="text-xs">{l.name || "Untitled Lead"}</SelectItem>
            ))}
          </SelectContent>
        </Select>

        {/* Sort Dropdown */}
        <Select value={sort} onValueChange={(v: string) => setSort(v as "newest" | "oldest")}>
          <SelectTrigger className="w-[120px] border-line bg-elevated text-xs text-ink-2 h-8">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="newest" className="text-xs">Newest first</SelectItem>
            <SelectItem value="oldest" className="text-xs">Oldest first</SelectItem>
          </SelectContent>
        </Select>

        {/* View Switcher (List vs Cards) */}
        <div className="flex items-center rounded-lg border border-line bg-soft p-0.5">
          <button
            type="button"
            onClick={() => setViewMode("list")}
            title="List view"
            aria-label="List view"
            className={cn(
              "flex h-7 w-7 items-center justify-center rounded-md text-xs transition-colors",
              viewMode === "list" ? "bg-elevated text-brand shadow-xs font-semibold" : "text-ink-3 hover:text-ink"
            )}
          >
            <LayoutList className="h-3.5 w-3.5" />
          </button>
          <button
            type="button"
            onClick={() => setViewMode("cards")}
            title="Card view"
            aria-label="Card view"
            className={cn(
              "flex h-7 w-7 items-center justify-center rounded-md text-xs transition-colors",
              viewMode === "cards" ? "bg-elevated text-brand shadow-xs font-semibold" : "text-ink-3 hover:text-ink"
            )}
          >
            <LayoutGrid className="h-3.5 w-3.5" />
          </button>
        </div>

        {/* Refresh */}
        <button 
          onClick={() => void refresh()} 
          title="Refresh notes" 
          className="flex h-8 w-8 items-center justify-center rounded-lg border border-line bg-elevated text-ink-2 hover:bg-soft hover:text-ink transition-colors"
        >
          <RefreshCw className="h-3.5 w-3.5" />
        </button>
      </div>

      {(error || query.error) && (
        <p role="alert" className="mt-2 text-xs text-red-600">
          {error || (query.error as Error).message}
        </p>
      )}

      {/* ── Content Area ────────────────────────────────────────── */}
      {query.isLoading ? (
        <div className="flex flex-1 items-center justify-center py-16 text-xs text-ink-3">
          Loading notes…
        </div>
      ) : filtered.length === 0 ? (
        <div className="mt-4 flex flex-col items-center gap-2 rounded-xl border border-line bg-elevated p-12 text-center">
          <StickyNote className="h-6 w-6 text-ink-3" />
          <p className="text-sm font-semibold text-ink">
            {rows.length === 0 ? "No notes yet" : "No notes match your filters"}
          </p>
          <p className="max-w-xs text-xs text-ink-3">
            {rows.length === 0 ? "Create your first note to capture lead context." : "Try adjusting your search query or lead filter."}
          </p>
        </div>
      ) : viewMode === "list" ? (
        /* ── Modern List / Feed View (Linear / Notion Style) ──── */
        <div className="mt-3 flex-1 overflow-y-auto min-h-0">
          <div className="rounded-xl border border-line bg-elevated divide-y divide-line/60 shadow-xs overflow-hidden">
            {visible.map(row => {
              const [title, body] = splitNoteTitle(row.notes);
              const leadName = leadNameOf(row);
              return (
                <div
                  key={row.id}
                  onClick={() => setEditing({ id: row.id, leadId: row.leadId, notes: row.notes })}
                  className="group flex flex-col sm:flex-row sm:items-center justify-between gap-3 px-4 py-3 hover:bg-soft/60 cursor-pointer transition-colors"
                >
                  {/* Left Column: Note Details */}
                  <div className="flex items-start gap-3 min-w-0 flex-1">
                    <div className="mt-0.5 flex h-7 w-7 shrink-0 items-center justify-center rounded-lg bg-soft text-brand border border-line/60">
                      <StickyNote className="h-3.5 w-3.5" />
                    </div>
                    <div className="min-w-0 flex-1 space-y-1">
                      <div className="flex flex-wrap items-center gap-2">
                        <span className="text-xs font-semibold text-ink group-hover:text-brand transition-colors">
                          {title || "Untitled note"}
                        </span>
                        {/* Lead Tag */}
                        <button
                          type="button"
                          onClick={(e) => {
                            e.stopPropagation();
                            navigate(`/crm/leads/${row.leadId}`);
                          }}
                          className="inline-flex items-center gap-1 rounded-md bg-soft px-2 py-0.5 text-[11px] font-medium text-ink-2 hover:bg-brand/10 hover:text-brand transition-colors"
                        >
                          <User className="h-3 w-3 text-ink-3" />
                          <span className="truncate max-w-[140px]">{leadName}</span>
                        </button>
                      </div>

                      {/* Content Snippet */}
                      {body && (
                        <p className="truncate text-xs text-ink-3 leading-relaxed max-w-2xl">
                          {body.replace(/\n+/g, " ")}
                        </p>
                      )}
                    </div>
                  </div>

                  {/* Right Column: Author, Date & Actions */}
                  <div className="flex items-center justify-between sm:justify-end gap-3 shrink-0 pt-1 sm:pt-0 border-t border-line/30 sm:border-t-0">
                    <div className="flex items-center gap-2 text-[11px] text-ink-3">
                      <span className="flex items-center gap-1.5">
                        <span className={cn("flex h-4 w-4 shrink-0 items-center justify-center rounded-full text-[8px] font-bold text-white", avatarColorOf(row.actorName || "?"))}>
                          {initialsOf(row.actorName || "?")}
                        </span>
                        <span className="text-ink-2 font-medium">{row.actorName || "Team"}</span>
                      </span>
                      <span className="text-ink-4">•</span>
                      <span className="flex items-center gap-1">
                        <Clock className="h-3 w-3 text-ink-4" />
                        <span>{ago(row.createdAt)}</span>
                      </span>
                    </div>

                    {/* Quick Action Buttons */}
                    <div className="flex items-center gap-1 opacity-80 sm:opacity-0 group-hover:opacity-100 transition-opacity" onClick={e => e.stopPropagation()}>
                      <button
                        type="button"
                        onClick={() => setEditing({ id: row.id, leadId: row.leadId, notes: row.notes })}
                        title="Edit note"
                        className="flex h-7 w-7 items-center justify-center rounded-md text-ink-3 hover:bg-soft hover:text-ink transition-colors"
                      >
                        <Pencil className="h-3.5 w-3.5" />
                      </button>
                      <button
                        type="button"
                        onClick={() => navigate(`/crm/leads/${row.leadId}`)}
                        title="Open lead record"
                        className="flex h-7 w-7 items-center justify-center rounded-md text-ink-3 hover:bg-soft hover:text-brand transition-colors"
                      >
                        <ExternalLink className="h-3.5 w-3.5" />
                      </button>
                      <button
                        type="button"
                        onClick={() => setDeleting(row)}
                        title="Delete note"
                        className="flex h-7 w-7 items-center justify-center rounded-md text-ink-3 hover:bg-red-500/10 hover:text-red-600 transition-colors"
                      >
                        <Trash2 className="h-3.5 w-3.5" />
                      </button>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      ) : (
        /* ── Adaptive Card / Memo View (Re-imagined from the rigid blocks) ──── */
        <div className="mt-3 flex-1 overflow-y-auto min-h-0">
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
            {visible.map(row => {
              const [title, body] = splitNoteTitle(row.notes);
              const leadName = leadNameOf(row);
              return (
                <div
                  key={row.id}
                  onClick={() => setEditing({ id: row.id, leadId: row.leadId, notes: row.notes })}
                  className="group relative flex flex-col justify-between rounded-xl border border-line bg-elevated p-3.5 hover:border-brand/40 hover:shadow-sm cursor-pointer transition-all duration-150 min-h-[140px]"
                >
                  <div className="space-y-2">
                    {/* Header: Lead badge & Quick Actions */}
                    <div className="flex items-center justify-between gap-2">
                      <button
                        type="button"
                        onClick={(e) => {
                          e.stopPropagation();
                          navigate(`/crm/leads/${row.leadId}`);
                        }}
                        className="inline-flex items-center gap-1 rounded-md bg-soft px-2 py-0.5 text-[11px] font-medium text-ink-2 hover:bg-brand/10 hover:text-brand transition-colors"
                      >
                        <User className="h-3 w-3 text-ink-3" />
                        <span className="truncate max-w-[130px]">{leadName}</span>
                      </button>

                      <div className="flex items-center gap-0.5" onClick={e => e.stopPropagation()}>
                        <button
                          type="button"
                          onClick={() => setEditing({ id: row.id, leadId: row.leadId, notes: row.notes })}
                          title="Edit"
                          className="flex h-6 w-6 items-center justify-center rounded text-ink-3 hover:bg-soft hover:text-ink transition-colors"
                        >
                          <Pencil className="h-3 w-3" />
                        </button>
                        <button
                          type="button"
                          onClick={() => setDeleting(row)}
                          title="Delete"
                          className="flex h-6 w-6 items-center justify-center rounded text-ink-3 hover:bg-red-500/10 hover:text-red-600 transition-colors"
                        >
                          <Trash2 className="h-3 w-3" />
                        </button>
                      </div>
                    </div>

                    {/* Title */}
                    <h3 className="text-xs font-semibold text-ink line-clamp-1 group-hover:text-brand transition-colors">
                      {title || "Untitled note"}
                    </h3>

                    {/* Body preview */}
                    {body ? (
                      <div className="text-xs text-ink-3 line-clamp-3 leading-relaxed">
                        <RichText text={body} />
                      </div>
                    ) : (
                      <p className="text-xs text-ink-4 italic">No additional body text</p>
                    )}
                  </div>

                  {/* Footer */}
                  <div className="mt-3 flex items-center justify-between border-t border-line/50 pt-2 text-[11px] text-ink-3">
                    <span className="flex items-center gap-1.5">
                      <span className={cn("flex h-4 w-4 shrink-0 items-center justify-center rounded-full text-[8px] font-bold text-white", avatarColorOf(row.actorName || "?"))}>
                        {initialsOf(row.actorName || "?")}
                      </span>
                      <span className="truncate font-medium text-ink-2">{row.actorName || "Team"}</span>
                    </span>
                    <span>{ago(row.createdAt)}</span>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* ── Pagination Footer ───────────────────────────────────── */}
      {filtered.length > 0 && (
        <div className="flex items-center justify-between pt-3 text-xs shrink-0 border-t border-line mt-2">
          <div className="flex items-center gap-1.5">
            <span className="text-ink-3 text-[11px]">Show:</span>
            {PAGE_SIZES.map(size => (
              <button
                key={size}
                onClick={() => { setPageSize(size); setPage(0); }}
                className={cn("rounded-md px-2 py-0.5 text-xs font-semibold transition-colors", pageSize === size ? "bg-soft text-ink border border-line" : "text-ink-3 hover:bg-soft")}
              >
                {size}
              </button>
            ))}
          </div>
          <div className="flex items-center gap-2 text-ink-3">
            <button 
              disabled={safePage === 0} 
              onClick={() => setPage(p => Math.max(0, p - 1))} 
              className="rounded-md px-2 py-0.5 font-semibold hover:bg-soft disabled:opacity-40 transition-colors"
            >
              ‹ Prev
            </button>
            <span>{`${safePage * pageSize + 1}–${Math.min(filtered.length, (safePage + 1) * pageSize)} of ${filtered.length}`}</span>
            <button 
              disabled={safePage >= pageCount - 1} 
              onClick={() => setPage(p => Math.min(pageCount - 1, p + 1))} 
              className="rounded-md px-2 py-0.5 font-semibold hover:bg-soft disabled:opacity-40 transition-colors"
            >
              Next ›
            </button>
          </div>
        </div>
      )}

      {/* ── Modals ──────────────────────────────────────────────── */}
      {(createOpen || editing) && (
        <NoteEditModal
          fixedLeadId={editing ? editing.leadId : null}
          leads={leads}
          note={editing}
          onClose={() => { setCreateOpen(false); setEditing(null); }}
          onSaved={() => { setCreateOpen(false); setEditing(null); void refresh(); }}
        />
      )}

      {deleting && (
        <ConfirmModal
          title="Delete note"
          body="Remove this note? This can't be undone."
          confirmLabel="Delete"
          onClose={() => setDeleting(null)}
          onConfirm={() => void confirmDelete()}
        />
      )}
    </div>
  );
}
